<template>
  <div>
    <el-upload class="uploader" ref="uploadRef" :show-file-list="false" action="" :accept="fileExt" :limit="1" :on-change="handleChange" :on-exceed="handleExceed" :http-request="customUpload" :before-upload="beforeUpload">
      <el-progress v-if="loading" :text-inside="true" :show-text="false" :stroke-width="12" :percentage="percentage" status="success"></el-progress>
      <i v-else class="el-icon-plus uploader-icon"></i>
    </el-upload>
  </div>
</template>

<script>
import SparkMD5 from 'spark-md5';

export default {
  name: 'storageChunkUpload',
  data() {
    return {
      loading: false,
      fileExt: '.jpg,.jpeg,.png,.gif,.rar,.zip,.pdf,.doc,.docx,.xls,.xlsx',
      chunkSize: 1 * 1024 * 1024, // 1MB
      percentage: 0,
      file: null
    };
  },
  methods: {
    //检查文件
    beforeUpload(file) {
      const parts = file.name.split('.');
      let ext = parts.length > 1 ? parts[parts.length - 1] : '';
      ext = ext.toLowerCase();
      if (!this.fileExt.includes(ext)) {
        this.$message.warning('请选择正确的文件格式');
        return false;
      }
      return true;
    },
    // 处理超出限制的情况：清除旧文件，让新文件能触发 on-change
    handleExceed(files, fileList) {
      console.log(files, fileList);
      this.$refs.uploadRef.clearFiles(); // 清除内部文件列表
      // 注意：清除后，通常需要用户重新选择，或者你可以手动触发逻辑
      // 但在 el-upload 中，clearFiles 后，当前的 files 参数中的新文件并不会自动加入
      // 所以最好的做法是提示用户，或者在这里不做操作，让用户重新点上传
      // 更优解：在 handleChange 中处理逻辑，而 handleExceed 仅用于清理状态
      this.$message.warning('当前只能上传一个文件，已自动替换');
      this.$refs.uploadRef.clearFiles();
      // 由于 clearFiles 后，本次选择的文件也被清除了，用户需要再次选择。
      // 如果想要“自动替换”体验，el-upload 比较难完美支持，通常建议用户重新选。
      // 或者：不调用 clearFiles，而是直接在 handleChange 里覆盖 this.file
    },

    handleChange(file, fileList) {
      // 每次选择新文件时，重置状态
      this.percentage = 0;
      this.file = file.raw;

      // 关键：如果 fileList 长度超过 1，手动清除多余的，保持内部状态干净
      if (fileList.length > 1) {
        this.$refs.uploadRef.clearFiles();
        // 注意：clearFiles 会把刚选的也清了，所以这里逻辑要小心
        // 更好的方式：只保留最后一个？el-upload 不支持直接操作 fileList
        // 实际上，只要 handleExceed 里清了，这里通常只会进来一个新的
      }
    },

    async customUpload(options) {
      const { file } = options;
      this.loading = true;
      try {
        // 计算 MD5
        const fileMd5 = await this.calculateMD5(file);
        if (!fileMd5 || fileMd5 === 'unknown') {
          this.$message.error('文件校验失败');
          return;
        }

        // 分片上传
        const chunks = Math.ceil(file.size / this.chunkSize);
        let num = 0;

        for (let i = 0; i < chunks; i++) {
          const start = i * this.chunkSize;
          const end = Math.min(start + this.chunkSize, file.size);
          const chunk = file.slice(start, end);

          const formData = new FormData();
          formData.append('file', chunk);
          formData.append('chunkIndex', i);
          formData.append('totalChunks', chunks);
          formData.append('fileMd5', fileMd5);

          const res = await this.$api.storage.chunkUpload(formData);
          if (res.code === 200) {
            num++;
            this.percentage = this.$numberNul(this.$numberDiv(num, chunks), 100);
          } else {
            throw new Error(res.msg || '分片上传失败');
          }
        }

        // 合并分片
        const mergeRes = await this.$api.storage.chunkMerge({ fileName: file.name, fileMd5: fileMd5, totalChunks: chunks });
        if (mergeRes.code === 200) {
          this.$message.success('上传成功');
          this.percentage = 100;
          this.$emit('change', mergeRes.data);

          // 上传成功后，务必清除文件列表，否则下次上传会被 limit 拦截
          this.$nextTick(() => {
            this.$refs.uploadRef.clearFiles();
          });
        } else {
          this.$message.error(mergeRes.msg || '合并失败');
        }
      } catch (error) {
        console.error('上传过程出错:', error);
        this.$message.error('上传失败: ' + error.message);
      } finally {
        this.loading = false;
      }
      this.loading = false;
    },

    calculateMD5(file) {
      return new Promise((resolve, reject) => {
        const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
        const chunkSize = this.chunkSize;
        const chunks = Math.ceil(file.size / chunkSize);

        if (chunks === 0) {
          resolve('empty-file');
          return;
        }

        const spark = new SparkMD5.ArrayBuffer();
        let currentChunk = 0;
        const fileReader = new FileReader();

        const loadNext = () => {
          const start = currentChunk * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          try {
            const chunk = blobSlice.call(file, start, end);
            fileReader.readAsArrayBuffer(chunk);
          } catch (e) {
            reject(e);
          }
        };

        fileReader.onload = e => {
          spark.append(e.target.result);
          currentChunk++;
          if (currentChunk < chunks) {
            loadNext();
          } else {
            const md5 = spark.end();
            resolve(md5);
          }
        };

        fileReader.onerror = e => {
          console.error('MD5计算 FileReader 错误', e);
          reject(new Error('MD5读取失败'));
        };

        loadNext();
      });
    }
  }
};
</script>

<style>
.uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  width: 100px;
  height: 100px;
  display: grid;
  align-items: center;
}
.uploader .el-upload:hover {
  border-color: #409eff;
}
.uploader-icon {
  font-size: 28px;
  color: #8c939d;
}
</style>
